// ATAD-146 — the Upload File modal's Alliant data sources: CMDA model scores,
// First Party customer lists and Syndicated Alliant segments.
//
// Alliant drops files into one shared folder per source. Nothing is uploaded
// from the user's computer here: they pick a file from that folder, name it
// and press Import, and the server imports THEIR OWN copy in a few minutes
// (server/services/modelFileImport.js). So the list shows every file in the
// folder, each with this user's own import status.
//
// CMDA files carry a 1–20 score and import as model files (kind 'model').
// The other sources hold only AIQ_HHID + AIQ_INDID and import as ordinary
// ID files (kind 'id', tagged with their dataSource), matched to people.
//
// The modal body is a <form> whose submit is the modal's Continue button
// (relabelled "Import" here). Enter in the search box is swallowed: a search
// must never start an import of whichever row happens to be selected.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';

import CollapsibleInstructions from '../CollapsibleInstructions';
import { UPLOAD_ERROR_TOAST } from '../FileUpload';
import { formatBytes } from '../FileUpload/utils';
import ErrorText from '../../ErrorText';
import FileStatusBadge from '../../FileStatusBadge';
import LoadingSpinner from '../../LoadingSpinner';
import ToastNotification from '../../ToastNotification';
import { formatDateCellShort } from '../../../helpers/reactTable';
import { FILES_QUERY, MODEL_FILE_SOURCES } from '../../../queries';
import { IMPORT_MODEL_FILE } from '../../../mutations';
import {
  SET_LOADING,
  SET_ON_CONTINUE,
  SET_SUCCESS,
} from '../../../reducers/actionReducer';
import { incrementName } from '../../../utils';

/** How often the list is re-read while one of the user's imports is running. */
export const SOURCES_POLL_MS = 5000;

/** Matches FileUpload: well past the 5 s success toasts, and hover pauses it. */
const ERROR_TOAST_MS = 10_000;

/** server/services/modelFiles.js MAX_MODEL_NAME_LENGTH. */
const MAX_NAME_LENGTH = 200;

const CONTINUE_LABEL = 'Import';

const GENERIC_IMPORT_ERROR = "We couldn't import that file. Try again.";

const iconProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

/** File.dataSource values; the server's source registry has the same ids. */
export type DataSourceId = 'cmda' | 'first-party' | 'syndicated-alliant';

type DataSourceCopy = {
  /** GraphQL `DataSource` enum value. */
  graphqlSource: 'CMDA' | 'FIRST_PARTY' | 'SYNDICATED_ALLIANT';
  /** 'model' files carry a score; 'id' files are matched like uploads. */
  kind: 'model' | 'id';
  /** The sources rail: title, subtitle and icon. */
  label: string;
  tagline: string;
  Icon: () => JSX.Element;
  /** The pane's heading and one line of body copy. */
  title: string;
  body: string;
  /** "No {plural} in the shared folder yet." */
  plural: string;
  /** Where the imported file shows up afterwards. */
  listedUnder: string;
  skippedRowsTitle: string;
};

/**
 * Everything the modal says about each source. Adding a source (Member
 * Transaction Files is next) means one entry here and one in the server's
 * registry (server/services/modelFileImport.js dataSourceConfig).
 */
export const DATA_SOURCES: Record<DataSourceId, DataSourceCopy> = {
  cmda: {
    graphqlSource: 'CMDA',
    kind: 'model',
    label: 'CMDA',
    tagline: 'Model scores',
    Icon: () => (
      <svg {...iconProps}>
        <path d="M3 3v18h18" />
        <path d="M8 16v-3M13 16V9M18 16V6" />
      </svg>
    ),
    title: 'CMDA models',
    body: 'Model files appear here when Alliant drops them in the shared folder.',
    plural: 'model files',
    listedUnder: 'Model Files',
    skippedRowsTitle:
      'Rows the import left out: no AIQ_HHID or AIQ_INDID, a score that is not a ' +
      'whole number from 1 to 20, or a repeat of an ID pair already in the file.',
  },
  'first-party': {
    graphqlSource: 'FIRST_PARTY',
    kind: 'id',
    label: 'First Party',
    tagline: 'Your own customer data',
    Icon: () => (
      <svg {...iconProps}>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: 'First Party data',
    body: 'Customer and target lists Alliant prepared for you. Importing one matches it to people, like an uploaded file.',
    plural: 'First Party files',
    listedUnder: 'Uploaded Files',
    skippedRowsTitle: 'Rows the import left out: no AIQ_HHID or AIQ_INDID.',
  },
  'syndicated-alliant': {
    graphqlSource: 'SYNDICATED_ALLIANT',
    kind: 'id',
    label: 'Syndicated Alliant',
    tagline: 'Ready-made audiences',
    Icon: () => (
      <svg {...iconProps}>
        <path d="m12 2 10 5-10 5L2 7l10-5z" />
        <path d="m2 17 10 5 10-5" />
        <path d="m2 12 10 5 10-5" />
      </svg>
    ),
    title: 'Syndicated Alliant',
    body: "Ready-made Alliant segments that aren't in the taxonomy. Importing one matches it to people, like an uploaded file.",
    plural: 'Syndicated Alliant files',
    listedUnder: 'Uploaded Files',
    skippedRowsTitle: 'Rows the import left out: no AIQ_HHID or AIQ_INDID.',
  },
};

/** The rail's order. */
export const DATA_SOURCE_IDS = Object.keys(DATA_SOURCES) as DataSourceId[];

/** latestImport on a ModelFileSource: the user's newest import of the file. */
export type ModelImportFile = {
  id: string;
  name: string;
  kind?: string | null;
  status: string;
  createdAt?: string | null;
  crawledAt?: string | null;
  errorMessage?: string | null;
  errorStage?: string | null;
  /**
   * Set once the import completed: people in the model (or unique ID pairs
   * in the file), the rows it skipped, and — for ID sources — how many of
   * those people matched.
   */
  numUniqueRecords?: number | null;
  numSkippedRecords?: number | null;
  numMatchedRecords?: number | null;
};

/** One file in a shared folder (GraphQL ModelFileSource). */
export type ModelFileSource = {
  key: string;
  /** The key relative to the shared folder, e.g. "sub/fans.csv.gz". */
  name: string;
  /** Bytes. A Float on the wire: model files outgrow GraphQL's Int. */
  size: number;
  lastModified?: string | null;
  /** The file was replaced since the user's import of it. */
  changedSinceImport: boolean;
  latestImport?: ModelImportFile | null;
};

export type ModelSourceState =
  | 'not-imported'
  | 'importing'
  | 'imported'
  | 'changed'
  | 'failed';

/** Where the user stands with one file in the folder. */
export function modelSourceState(source: ModelFileSource): ModelSourceState {
  const latest = source.latestImport;
  if (!latest) return 'not-imported';
  if (latest.status === 'processing-failed') return 'failed';
  if (latest.status === 'completed') {
    return source.changedSinceImport ? 'changed' : 'imported';
  }
  // pending / processing / matching — and any status this build doesn't
  // know, which must not be offered for re-import while it might still be
  // running.
  return 'importing';
}

/**
 * Rows an import can start from: never imported, replaced since the user's
 * import, or a failed import (the server replaces the failed one). An
 * import in progress or already done for this version of the file can't.
 */
export function canImportModelSource(source: ModelFileSource): boolean {
  const state = modelSourceState(source);
  return state === 'not-imported' || state === 'changed' || state === 'failed';
}

/** "sub/Man City Fans.csv.gz" → "Man City Fans" (the server's modelNameFromKey). */
export function modelNameFromFileName(fileName: string): string {
  const base = String(fileName ?? '').split('/').pop() ?? '';
  return base.replace(/\.csv(\.gz)?$/i, '');
}

/**
 * The Name a selected row starts with: the file name without folders or
 * extension. A changed file's earlier import still holds that name (names
 * are unique per user), so it is suggested as "Name (1)" rather than as a
 * guaranteed "name taken" error.
 */
export function suggestedModelName(source: ModelFileSource): string {
  const name = modelNameFromFileName(source.name);
  if (modelSourceState(source) === 'changed' && source.latestImport?.name === name) {
    return incrementName(name);
  }
  return name;
}

export function modelImportStartedMessage(
  name: string,
  dataSource: DataSourceId = 'cmda'
): string {
  return `Importing ${name}. It'll be ready in a few minutes under ${DATA_SOURCES[dataSource].listedUnder}.`;
}

const formatDate = (value?: string | null) =>
  formatDateCellShort({ value, ifEmpty: '' }) || '';

const formatCount = (n: number) => n.toLocaleString('en-US');

/**
 * What a completed import kept and what it dropped. The import skips bad
 * rows rather than failing (it only fails above 10% skipped), so a file
 * that imported fine can still be missing rows the user should know about.
 * CMDA counts people in the model; ID sources count people matched.
 */
function ImportSummary({
  latest,
  copy,
}: {
  latest: ModelImportFile;
  copy: DataSourceCopy;
}) {
  if (latest.status !== 'completed') return null;
  const count = copy.kind === 'id' ? latest.numMatchedRecords : latest.numUniqueRecords;
  if (count == null) return null;
  const skipped = latest.numSkippedRecords ?? 0;
  return (
    <span className="break-words">
      <span>
        {formatCount(count)} {copy.kind === 'id' ? 'matched' : 'people'}
      </span>
      {skipped > 0 && (
        <span className="text-orange" title={copy.skippedRowsTitle}>
          {' · '}
          {formatCount(skipped)} {skipped === 1 ? 'row' : 'rows'} skipped
        </span>
      )}
    </span>
  );
}

/** The right-hand meta of a row: size and date, then the user's import. */
function RowMeta({
  source,
  copy,
  onToggleError,
}: {
  source: ModelFileSource;
  copy: DataSourceCopy;
  onToggleError: (event?: React.MouseEvent) => void;
}) {
  const latest = source.latestImport;
  const state = modelSourceState(source);
  const added = formatDate(source.lastModified);

  return (
    <span className="flex flex-col items-end gap-0.5 text-[13px] text-gray-600 text-right flex-shrink-0">
      <span className="whitespace-nowrap tabular-nums">
        {formatBytes(source.size)}
        {added ? ` · ${added}` : ''}
      </span>
      {latest && state === 'changed' && (
        <span className="text-orange">Changed since your import</span>
      )}
      {latest && (state === 'importing' || state === 'failed') && (
        <FileStatusBadge
          status={latest.status}
          kind={latest.kind ?? copy.kind}
          imported
          onViewError={state === 'failed' ? onToggleError : undefined}
        />
      )}
      {latest && <ImportSummary latest={latest} copy={copy} />}
    </span>
  );
}

type DataSourceImportProps = {
  dispatchAction: (action: any) => void;
  /** Which shared folder to list and import from. */
  source?: DataSourceId;
};

export function DataSourceImport({ dispatchAction, source: dataSource = 'cmda' }: DataSourceImportProps) {
  const copy = DATA_SOURCES[dataSource];
  const apolloClient = useApolloClient();
  const { data, loading, error, startPolling, stopPolling } = useQuery(
    MODEL_FILE_SOURCES,
    {
      variables: { source: copy.graphqlSource },
      fetchPolicy: 'cache-and-network',
    }
  );
  const [importModelFile] = useMutation(IMPORT_MODEL_FILE);

  const sources: ModelFileSource[] = useMemo(
    () => data?.modelFileSources ?? [],
    [data]
  );

  const [search, setSearch] = useState('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [openErrorKey, setOpenErrorKey] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [toastShown, setToastShown] = useState(false);
  const importingRef = useRef(false);
  const errorTextRef = useRef<HTMLDivElement>(null);

  // Re-read the folder only while one of the user's imports is queued or
  // running, so its row moves on to Imported (or failed) by itself.
  const anyImporting = sources.some(
    (source) => modelSourceState(source) === 'importing'
  );
  useEffect(
    function pollWhileImporting() {
      if (!anyImporting) return undefined;
      startPolling(SOURCES_POLL_MS);
      return () => stopPolling();
    },
    [anyImporting, startPolling, stopPolling]
  );

  // A selection the list no longer offers (it refreshed underneath) is gone.
  const selected =
    sources.find(
      (source) => source.key === selectedKey && canImportModelSource(source)
    ) ?? null;
  const trimmedName = name.trim();

  const query = search.trim().toLowerCase();
  const visibleSources = useMemo(() => {
    if (!query) return sources;
    return sources.filter(
      (source) =>
        source.name.toLowerCase().includes(query) ||
        (source.latestImport?.name ?? '').toLowerCase().includes(query)
    );
  }, [sources, query]);

  const clearImportError = () => {
    setImportError(null);
    setToastShown(false);
  };

  function selectSource(source: ModelFileSource) {
    if (!canImportModelSource(source) || source.key === selectedKey) return;
    setSelectedKey(source.key);
    setName(suggestedModelName(source));
    clearImportError();
  }

  const runImport = useCallback(async () => {
    if (!selected || !trimmedName || importingRef.current) return;
    importingRef.current = true;
    setImportError(null);
    setToastShown(false);
    // Dispatched by hand rather than mirrored from the mutation's `loading`,
    // so the modal can't stay stuck on "Loading..." if the user switches
    // source mid-request and this component unmounts.
    dispatchAction({ type: SET_LOADING, payload: { loading: true } });
    try {
      const result = await importModelFile({
        variables: {
          sourceKey: selected.key,
          name: trimmedName,
          source: copy.graphqlSource,
        },
      });
      const file = result?.data?.importModelFile;
      // The app's Apollo client sends mutations with errorPolicy 'all'
      // (hooks/useAuth.tsx): a refused import RESOLVES with `errors` instead
      // of throwing, and the server's message there is the one to show.
      if (!file) throw new Error(result?.errors?.[0]?.message || GENERIC_IMPORT_ERROR);

      // Before SET_SUCCESS: that closes the modal, and the folder list is
      // only refetched while something is still watching it.
      apolloClient.refetchQueries({ include: [FILES_QUERY, MODEL_FILE_SOURCES] });
      dispatchAction({
        type: SET_SUCCESS,
        payload: { message: modelImportStartedMessage(file.name || trimmedName, dataSource) },
      });
    } catch (err) {
      setImportError((err as Error)?.message || GENERIC_IMPORT_ERROR);
      setToastShown(true);
    } finally {
      importingRef.current = false;
      dispatchAction({ type: SET_LOADING, payload: { loading: false } });
    }
  }, [selected, trimmedName, importModelFile, apolloClient, dispatchAction, copy, dataSource]);

  // The modal's Continue button, labelled Import. Disabled until an
  // importable row is selected and has a name.
  useEffect(() => {
    dispatchAction({
      type: SET_ON_CONTINUE,
      payload: {
        onContinue: selected && trimmedName ? runImport : null,
        continueLabel: CONTINUE_LABEL,
      },
    });
  }, [dispatchAction, selected, trimmedName, runImport]);

  useEffect(
    function bringErrorIntoView() {
      // Optional call: jsdom has no scrollIntoView.
      if (importError) {
        errorTextRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
      }
    },
    [importError]
  );

  let list: React.ReactNode;
  if (sources.length === 0) {
    list = loading ? (
      <LoadingSpinner message={`Looking for ${copy.plural}...`} />
    ) : error ? (
      <ErrorText message={error.message} />
    ) : (
      <p className="text-center py-8 text-gray-500">
        No {copy.plural} in the shared folder yet.
      </p>
    );
  } else if (visibleSources.length === 0) {
    list = (
      <p className="text-center py-8 text-gray-500">
        No {copy.plural} match "{search}".
      </p>
    );
  } else {
    list = (
      <ul
        className="flex flex-col border border-gray-200 rounded-[10px] overflow-hidden divide-y divide-gray-200"
        aria-label={copy.title}
      >
        {visibleSources.map((source) => {
          const selectable = canImportModelSource(source);
          const isSelected = selected?.key === source.key;
          const latest = source.latestImport;
          const importedOn = formatDate(latest?.createdAt);
          return (
            <li
              key={source.key}
              data-source-row={source.key}
              onClick={() => selectSource(source)}
              className={[
                'flex flex-col px-4',
                selectable ? 'cursor-pointer' : '',
                isSelected
                  ? 'bg-[#f0fdfa]'
                  : selectable
                    ? 'bg-white hover:bg-gray-50'
                    : 'bg-white',
              ].join(' ')}
            >
              <div className="flex items-center gap-3 min-h-[52px] py-2">
                <input
                  type="radio"
                  name="data-source-file"
                  checked={isSelected}
                  disabled={!selectable}
                  onChange={() => selectSource(source)}
                  aria-label={`Select ${source.name}`}
                  className="peer sr-only"
                />
                <span
                  aria-hidden="true"
                  className={`w-4 h-4 rounded-full flex-shrink-0 box-border peer-focus-visible:ring-2 peer-focus-visible:ring-primary peer-focus-visible:ring-offset-2 ${
                    isSelected
                      ? 'border-[5px] border-primary-dark'
                      : selectable
                        ? 'border-2 border-gray-400'
                        : 'border-2 border-gray-200'
                  }`}
                />
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span
                    className={`text-sm font-medium break-all ${
                      selectable ? 'text-gray-800' : 'text-gray-500'
                    }`}
                  >
                    {source.name}
                  </span>
                  {latest && (
                    <span className="text-xs text-gray-500 break-all">
                      Imported as {latest.name}
                      {importedOn ? ` · ${importedOn}` : ''}
                    </span>
                  )}
                </span>
                <RowMeta
                  source={source}
                  copy={copy}
                  onToggleError={(event?: React.MouseEvent) => {
                    // Reading the error isn't choosing the row.
                    event?.stopPropagation();
                    setOpenErrorKey((open) =>
                      open === source.key ? null : source.key
                    );
                  }}
                />
              </div>
              {modelSourceState(source) === 'failed' && openErrorKey === source.key && (
                <p className="pb-3 pl-7 text-xs text-error break-words">
                  {latest?.errorMessage || 'No error message was captured for this import.'}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <>
      <div className="w-full flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold text-gray-800">{copy.title}</h3>
          <p className="text-sm text-gray-600">{copy.body}</p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2 min-h-[44px] px-3 border border-gray-300 rounded-lg text-gray-500 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                // Enter would submit the modal's form, i.e. press Import.
                if (e.key === 'Enter') e.preventDefault();
              }}
              placeholder="Search"
              aria-label={`Search ${copy.plural}`}
              className="flex-1 border-0 p-0 text-sm text-gray-800 bg-transparent focus:outline-none focus:ring-0"
            />
          </label>
          {query && sources.length > 0 && (
            <p className="text-xs text-gray-500">
              {visibleSources.length} of {sources.length} files
            </p>
          )}
        </div>

        {list}

        {selected && (
          <div className="flex flex-col gap-1">
            <label
              htmlFor="data-source-import-name"
              className="text-sm font-medium text-gray-700"
            >
              Name
            </label>
            <input
              id="data-source-import-name"
              type="text"
              value={name}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => {
                setName(e.target.value);
                clearImportError();
              }}
              onKeyDown={(e) => {
                // Enter imports, whether or not the search box is also on
                // screen (two text fields switch off a form's implicit submit).
                if (e.key === 'Enter') {
                  e.preventDefault();
                  runImport();
                }
              }}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <p className="text-xs text-gray-500 break-all">
              How the file is listed under {copy.listedUnder} in the Audience
              Builder. Importing {selected.name}.
            </p>
          </div>
        )}

        {importError && (
          <div ref={errorTextRef}>
            <ErrorText className="mt-2" message={importError} />
          </div>
        )}

        <CollapsibleInstructions kind={copy.kind === 'model' ? 'model' : 'id-source'} />
      </div>
      <ToastNotification
        message={UPLOAD_ERROR_TOAST}
        error={true}
        show={toastShown}
        duration={ERROR_TOAST_MS}
        onDismiss={() => setToastShown(false)}
        position="top-center"
      />
    </>
  );
}

export default DataSourceImport;
